import { useEffect, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SectionLabel } from "@/components/SectionLabel";
import { KineticHeading } from "@/components/Reveal";

export function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          const y = el.getBoundingClientRect().top + window.pageYOffset - 90;
          window.scrollTo({ top: y, behavior: "smooth" });
        }
      }, 350);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

// Interior page hero
export function PageHero({ n, label, lines, intro, compact = false }) {
  return (
    <section className={`relative ${compact ? "pt-12 md:pt-16" : "pt-40 md:pt-52"} pb-16 md:pb-24 max-w-[1600px] mx-auto px-6 md:px-10`} data-testid="page-hero">
      <div className="font-mono-label text-xs tracking-label uppercase flex items-center gap-3" data-testid="page-hero-label">
        <span className="text-logo-blue">✦</span>
        <span className="text-logo-blue">{label}</span>
      </div>
      <KineticHeading
        lines={lines}
        delay={0.15}
        className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white tracking-tight leading-[1.02] mt-8 max-w-6xl font-light"
      />
      {intro && (
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="text-white/60 text-lg md:text-xl leading-relaxed max-w-2xl mt-10"
        >
          {intro}
        </motion.p>
      )}
    </section>
  );
}

export function Stat({ value, suffix = "", label, big = false }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const dur = 1400;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);
  return (
    <div ref={ref} data-testid="stat">
      <div className={`font-display text-accent tracking-tight ${big ? "text-6xl md:text-8xl" : "text-5xl md:text-6xl"}`}>
        {n.toLocaleString("en-IN")}{suffix}
      </div>
      <div className="font-mono-label text-[11px] tracking-label uppercase text-white/50 mt-3">{label}</div>
    </div>
  );
}

export function CTASection({ dark = true }) {
  return (
    <section className="bg-accent text-[#050B14]" data-testid="cta-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-32 grid md:grid-cols-12 gap-10 items-end">
        <div className="md:col-span-8">
          <p className="font-mono-label text-[11px] tracking-label uppercase mb-6">Engage to Learn & Learn to Innovate</p>
          <h2 className="font-display text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02] font-light">
            Bring the PBL-TDLE framework to your school.
          </h2>
        </div>
        <div className="md:col-span-4 flex flex-col gap-4">
          <Link to="/enroll" data-testid="cta-enroll" className="group flex items-center justify-between border-b-2 border-[#050B14] pb-3 font-mono-label text-sm tracking-label uppercase hover:pl-2 transition-all">
            Enroll Your School <ArrowUpRight className="group-hover:rotate-45 transition-transform" />
          </Link>
          <Link to="/become-a-tutor" data-testid="cta-tutor" className="group flex items-center justify-between border-b-2 border-[#050B14]/40 pb-3 font-mono-label text-sm tracking-label uppercase hover:pl-2 transition-all">
            Become a Tutor <ArrowUpRight className="group-hover:rotate-45 transition-transform" />
          </Link>
          <Link to="/contact" data-testid="cta-contact" className="group flex items-center justify-between border-b-2 border-[#050B14]/40 pb-3 font-mono-label text-sm tracking-label uppercase hover:pl-2 transition-all">
            Talk to Us <ArrowUpRight className="group-hover:rotate-45 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
}
